import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileUp, Save, Send, Trash2 } from 'lucide-react';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  createTicketDraft,
  getCatalogService,
  getTicket,
  removeTicketAttachment,
  submitTicketDraft,
  updateTicketDraft,
  uploadTicketAttachment,
  getCauTicketAccess,
  linkCauAsset,
  upsertCauAccess,
  type CauAccessInput,
  type CauFormField,
  type CauTicketLevel,
} from '../api';
import { cauFieldLabel } from '../labels';

function message(error: unknown) {
  const value = error as { response?: { data?: { message?: string | { message?: string; issues?: string[]; missing?: string[] } } }; message?: string };
  const body = value.response?.data?.message;
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') {
    const details = body.issues ?? body.missing;
    return `${body.message ?? 'Dados inválidos'}${details?.length ? `: ${details.join(', ')}` : ''}`;
  }
  return value.message ?? 'Não foi possível concluir a operação.';
}

export default function CauOpenTicketPage() {
  const { serviceVersionId = '', draftId = '' } = useParams();
  const [params] = useSearchParams();
  const prelinkedAssetId = params.get('assetId');
  const navigate = useNavigate();
  const client = useQueryClient();
  const draft = useQuery({ queryKey: ['cau', 'ticket', draftId], queryFn: () => getTicket(draftId), enabled: Boolean(draftId) });
  const resolvedServiceVersionId = serviceVersionId || draft.data?.serviceVersionId || '';
  const service = useQuery({
    queryKey: ['cau', 'catalog', resolvedServiceVersionId],
    queryFn: () => getCatalogService(resolvedServiceVersionId),
    enabled: Boolean(resolvedServiceVersionId),
  });
  const [currentDraftId, setCurrentDraftId] = useState(draftId);
  const [impact, setImpact] = useState<CauTicketLevel>('medium');
  const [urgency, setUrgency] = useState<CauTicketLevel>('medium');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [logicalName, setLogicalName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [hasAccessRequest, setHasAccessRequest] = useState(false);
  const [access, setAccess] = useState<CauAccessInput>({ operation: 'create', accessType: 'regular', systemCode: '', systemName: '', profileCode: '', profileName: '', scope: {}, riskLevel: 'medium', justification: '', requiresMfa: false, mfaAvailable: false, monitoringRequired: false, emergencyExecution: false });
  const existingAccess = useQuery({ queryKey: ['cau','ticket',currentDraftId,'access'], queryFn: () => getCauTicketAccess(currentDraftId), enabled: Boolean(currentDraftId) });

  useEffect(() => {
    if (!draft.data || initialized) return;
    setCurrentDraftId(draft.data.id);
    setImpact(draft.data.impact);
    setUrgency(draft.data.urgency);
    setFormData(draft.data.formData ?? {});
    setInitialized(true);
  }, [draft.data, initialized]);
  useEffect(() => {
    if (!existingAccess.data || hasAccessRequest) return;
    const item = existingAccess.data;
    setHasAccessRequest(true);
    setAccess({ operation: item.operation, accessType: item.accessType, systemCode: item.systemCode, systemName: item.systemName, profileCode: item.profileCode, profileName: item.profileName, scope: item.scope, riskLevel: item.riskLevel, justification: item.justification, validFrom: item.validFrom ?? undefined, validUntil: item.validUntil ?? undefined, requiresMfa: item.requiresMfa, mfaAvailable: item.mfaAvailable, monitoringRequired: item.monitoringRequired, emergencyExecution: item.emergencyExecution });
  }, [existingAccess.data, hasAccessRequest]);

  const upload = useMutation({
    mutationFn: async () => {
      if (!selectedFile || !logicalName) throw new Error('Selecione o documento e o arquivo.');
      const id = await persistDraft();
      return uploadTicketAttachment(id, selectedFile, logicalName);
    },
    onSuccess: async () => {
      setSelectedFile(null); setNotice('Anexo salvo e validado.'); setFailure(null);
      await client.invalidateQueries({ queryKey: ['cau', 'ticket', currentDraftId || draftId] });
    },
    onError: (error) => setFailure(message(error)),
  });

  async function persistDraft() {
    if (!resolvedServiceVersionId) throw new Error('Serviço não identificado.');
    const payload = { impact, urgency, formData };
    const saved = currentDraftId
      ? await updateTicketDraft(currentDraftId, payload)
      : await createTicketDraft({ serviceVersionId: resolvedServiceVersionId, ...payload });
    if (!currentDraftId) {
      setCurrentDraftId(saved.id);
      navigate(`/cau/chamados/rascunho/${saved.id}`, { replace: true });
    }
    if (hasAccessRequest) await upsertCauAccess(saved.id, access);
    if (prelinkedAssetId) await linkCauAsset(saved.id, prelinkedAssetId, true);
    await client.invalidateQueries({ queryKey: ['cau', 'tickets'] });
    return saved.id;
  }

  async function save(event?: FormEvent) {
    event?.preventDefault();
    try {
      await persistDraft(); setNotice('Rascunho salvo. Nenhum protocolo foi gerado ainda.'); setFailure(null);
    } catch (error) { setFailure(message(error)); }
  }

  async function submit() {
    try {
      const id = await persistDraft();
      const sent = await submitTicketDraft(id);
      setFailure(null);
      navigate(`/cau/chamados/${sent.id}`);
    } catch (error) { setFailure(message(error)); }
  }

  function changeField(field: CauFormField, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    const target = event.target as HTMLInputElement;
    let value: unknown = target.value;
    if (field.type === 'number') value = target.value === '' ? undefined : Number(target.value);
    if (field.type === 'boolean') value = target.checked;
    setFormData((current) => ({ ...current, [field.key]: value }));
  }

  async function removeAttachment(id: string) {
    if (!currentDraftId) return;
    await removeTicketAttachment(currentDraftId, id);
    await client.invalidateQueries({ queryKey: ['cau', 'ticket', currentDraftId] });
  }

  if (service.isLoading || (draftId && draft.isLoading)) return <div className="p-6 text-gray-500">Carregando solicitação…</div>;
  if (!service.data || draft.isError) return <div className="p-6 text-red-700">Serviço ou rascunho indisponível.</div>;
  const definition = service.data;
  const attachments = draft.data?.attachments ?? [];
  const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100';

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <Link to="/cau" className="mb-4 inline-flex items-center gap-2 text-sm text-blue-700"><ArrowLeft className="h-4 w-4" />Voltar ao catálogo</Link>
      <form onSubmit={save} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h1 className="text-2xl font-semibold">Abrir solicitação</h1>
        <p className="mt-1 text-sm text-gray-600">{definition.name} • {definition.category.name}</p>
        <p className="mt-3 rounded-lg bg-blue-50 p-3 text-sm text-blue-900">O protocolo será gerado somente ao enviar. Você pode salvar e continuar o rascunho depois.</p>
        {prelinkedAssetId && <p className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900">O ativo selecionado será vinculado automaticamente ao chamado.</p>}
        {notice && <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">{notice}</div>}
        {failure && <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">{failure}</div>}

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-medium">Impacto
            <select className={`${inputClass} mt-1`} value={impact} onChange={(e) => setImpact(e.target.value as CauTicketLevel)}>
              <option value="low">Baixo</option><option value="medium">Médio</option><option value="high">Alto</option><option value="critical">Crítico</option>
            </select>
          </label>
          <label className="text-sm font-medium">Urgência
            <select className={`${inputClass} mt-1`} value={urgency} onChange={(e) => setUrgency(e.target.value as CauTicketLevel)}>
              <option value="low">Baixa</option><option value="medium">Média</option><option value="high">Alta</option><option value="critical">Crítica</option>
            </select>
          </label>
        </div>

        <section className="mt-6 space-y-4">
          <h2 className="font-semibold">Informações da solicitação</h2>
          {definition.formSchema?.fields.map((field) => <label key={field.key} className="block text-sm font-medium">{cauFieldLabel(field.key,definition.formSchema.fields)}{field.required && <span className="text-red-600"> *</span>}
            {field.type === 'textarea' ? <textarea className={`${inputClass} mt-1 min-h-24`} value={String(formData[field.key] ?? '')} onChange={(e) => changeField(field, e)} />
              : field.type === 'select' ? <select className={`${inputClass} mt-1`} value={String(formData[field.key] ?? '')} onChange={(e) => changeField(field, e)}><option value="">Selecione</option>{field.options?.map((option) => <option key={option}>{option}</option>)}</select>
              : field.type === 'boolean' ? <span className="ml-3 inline-flex items-center gap-2"><input type="checkbox" checked={Boolean(formData[field.key])} onChange={(e) => changeField(field, e)} />Sim</span>
              : <input className={`${inputClass} mt-1`} type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} value={String(formData[field.key] ?? '')} onChange={(e) => changeField(field, e)} />}
          </label>)}
        </section>

        <section className="mt-7 border-t pt-5">
          <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={hasAccessRequest} onChange={(e) => setHasAccessRequest(e.target.checked)} />Esta solicitação cria, altera ou revoga um acesso</label>
          {hasAccessRequest && <div className="mt-3 grid gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4 sm:grid-cols-2">
            <label className="text-sm">Operação<select className={`${inputClass} mt-1`} value={access.operation} onChange={(e) => setAccess({...access,operation:e.target.value as CauAccessInput['operation']})}><option value="create">Criar</option><option value="change">Alterar</option><option value="revoke">Revogar</option></select></label>
            <label className="text-sm">Tipo<select className={`${inputClass} mt-1`} value={access.accessType} onChange={(e) => setAccess({...access,accessType:e.target.value as CauAccessInput['accessType']})}><option value="regular">Regular</option><option value="special">Especial</option><option value="privileged">Privilegiado</option><option value="temporary">Temporário</option><option value="emergency">Emergencial</option></select></label>
            <label className="text-sm">Código do sistema<input required className={`${inputClass} mt-1`} value={access.systemCode} onChange={(e)=>setAccess({...access,systemCode:e.target.value})}/></label>
            <label className="text-sm">Sistema<input required className={`${inputClass} mt-1`} value={access.systemName} onChange={(e)=>setAccess({...access,systemName:e.target.value})}/></label>
            <label className="text-sm">Código do perfil<input required className={`${inputClass} mt-1`} value={access.profileCode} onChange={(e)=>setAccess({...access,profileCode:e.target.value})}/></label>
            <label className="text-sm">Perfil solicitado<input required className={`${inputClass} mt-1`} value={access.profileName} onChange={(e)=>setAccess({...access,profileName:e.target.value})}/></label>
            <label className="text-sm">Risco<select className={`${inputClass} mt-1`} value={access.riskLevel} onChange={(e)=>setAccess({...access,riskLevel:e.target.value as CauAccessInput['riskLevel']})}><option value="low">Baixo</option><option value="medium">Médio</option><option value="high">Alto</option><option value="critical">Crítico</option></select></label>
            <label className="text-sm">Validade final<input className={`${inputClass} mt-1`} type="datetime-local" value={access.validUntil?.slice(0,16)??''} onChange={(e)=>setAccess({...access,validUntil:e.target.value?new Date(e.target.value).toISOString():undefined})}/></label>
            <label className="text-sm sm:col-span-2">Escopo (descrição)<input required className={`${inputClass} mt-1`} value={String(access.scope.description??'')} onChange={(e)=>setAccess({...access,scope:{description:e.target.value}})}/></label>
            <label className="text-sm sm:col-span-2">Justificativa<textarea required className={`${inputClass} mt-1`} value={access.justification} onChange={(e)=>setAccess({...access,justification:e.target.value})}/></label>
            <div className="flex flex-wrap gap-4 text-sm sm:col-span-2"><label><input type="checkbox" checked={access.requiresMfa} onChange={(e)=>setAccess({...access,requiresMfa:e.target.checked})}/> Exigir MFA</label><label><input type="checkbox" checked={access.mfaAvailable} onChange={(e)=>setAccess({...access,mfaAvailable:e.target.checked})}/> MFA disponível</label><label><input type="checkbox" checked={access.monitoringRequired} onChange={(e)=>setAccess({...access,monitoringRequired:e.target.checked})}/> Monitoramento obrigatório</label><label><input type="checkbox" checked={access.emergencyExecution} onChange={(e)=>setAccess({...access,emergencyExecution:e.target.checked})}/> Execução emergencial</label></div>
          </div>}
        </section>

        <section className="mt-7 border-t pt-5">
          <h2 className="font-semibold">Documentos</h2>
          <p className="mt-1 text-xs text-gray-500">PDF, imagens, Word, Excel ou XML; máximo de 20 MB. A assinatura real do arquivo é validada.</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <select className={inputClass} value={logicalName} onChange={(e) => setLogicalName(e.target.value)}>
              <option value="">Tipo de documento</option>
              {definition.documentRequirements.map((doc) => <option key={doc.name} value={doc.name}>{doc.name}{doc.required ? ' *' : ''}</option>)}
              <option value="Outro documento">Outro documento</option>
            </select>
            <input className={inputClass} type="file" onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)} />
            <button type="button" disabled={upload.isPending} onClick={() => upload.mutate()} className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-300 px-3 py-2 text-sm text-blue-700"><FileUp className="h-4 w-4" />Anexar</button>
          </div>
          <div className="mt-3 space-y-2">{attachments.map((attachment) => <div key={attachment.id} className="flex items-center justify-between rounded border p-2 text-sm"><span>{attachment.logicalName}: {attachment.fileName} • v{attachment.version} • {attachment.scanStatus === 'clean' ? 'liberado' : attachment.scanStatus}</span><button type="button" onClick={() => removeAttachment(attachment.id)} className="text-red-700"><Trash2 className="h-4 w-4" /></button></div>)}</div>
        </section>

        <div className="mt-7 flex flex-wrap justify-end gap-3">
          <button type="submit" className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm"><Save className="h-4 w-4" />Salvar rascunho</button>
          <button type="button" onClick={submit} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white"><Send className="h-4 w-4" />Enviar solicitação</button>
        </div>
      </form>
    </div>
  );
}
